/**
 * CIPA-Compliant Consent Management System
 * 
 * This module ensures compliance with the California Invasion of Privacy Act (CIPA)
 * by managing user consent before any tracking scripts are loaded or data is transmitted.
 * 
 * Key Compliance Features:
 * - No scripts fire before explicit consent
 * - Granular consent categories
 * - Hard blocking of tracking on decline
 * - Consent verification before any data transmission
 */

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing' | 'personalization';

export interface ConsentState {
  necessary: boolean; // Always true - required for site functionality
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  timestamp: string | null;
  version: string; // For tracking consent policy versions
}

export interface ConsentEvent {
  category: ConsentCategory;
  granted: boolean;
  timestamp: string;
}

type ConsentCallback = (state: ConsentState) => void;

const CONSENT_KEY = 'flashread_consent_v2';
const CONSENT_VERSION = '2.0.0'; // Increment when privacy policy changes

// Default state - only necessary cookies allowed
const DEFAULT_CONSENT: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
  personalization: false,
  timestamp: null,
  version: CONSENT_VERSION,
};

class ConsentManager {
  private state: ConsentState;
  private listeners: Set<ConsentCallback> = new Set();
  private initialized: boolean = false;

  constructor() {
    this.state = this.loadConsent();
    this.initialized = true;
  }

  /**
   * Load consent from storage with validation
   */
  private loadConsent(): ConsentState {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ConsentState;
        
        // Validate structure
        if (this.isValidConsentState(parsed)) {
          // Check if consent version matches current policy
          if (parsed.version !== CONSENT_VERSION) {
            // Policy changed - require re-consent
            return { ...DEFAULT_CONSENT };
          }
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load consent state:', e);
    }
    return { ...DEFAULT_CONSENT };
  }

  /**
   * Validate consent state structure
   */
  private isValidConsentState(state: any): state is ConsentState {
    return (
      state &&
      typeof state === 'object' &&
      typeof state.necessary === 'boolean' &&
      typeof state.analytics === 'boolean' &&
      typeof state.marketing === 'boolean' &&
      typeof state.personalization === 'boolean'
    );
  }

  /**
   * Save consent to storage
   */
  private saveConsent(): void {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.error('Failed to save consent state:', e);
    }
  }

  /**
   * Get current consent state
   */
  getState(): ConsentState {
    return { ...this.state };
  }

  /**
   * Check if a specific category is consented
   */
  isConsented(category: ConsentCategory): boolean {
    return this.state[category];
  }

  /**
   * Check if user has made any consent choice
   */
  hasConsentChoice(): boolean {
    return this.state.timestamp !== null;
  }

  /**
   * Grant consent for specific categories
   */
  grantConsent(categories: ConsentCategory[]): void {
    const previousState = { ...this.state };
    
    categories.forEach(category => {
      if (category !== 'necessary') {
        this.state[category] = true;
      }
    });

    this.state.timestamp = new Date().toISOString();
    this.state.version = CONSENT_VERSION;
    
    this.saveConsent();
    this.notifyListeners();
    this.logConsentEvent(categories, true);
  }

  /**
   * Deny consent for specific categories
   */
  denyConsent(categories: ConsentCategory[]): void {
    categories.forEach(category => {
      if (category !== 'necessary') {
        this.state[category] = false;
      }
    });

    this.state.timestamp = new Date().toISOString();
    this.state.version = CONSENT_VERSION;
    
    this.saveConsent();
    this.notifyListeners();
    this.logConsentEvent(categories, false);

    // Trigger cleanup of existing tracking data
    this.cleanupDeniedData(categories);
  }

  /**
   * Grant all consent categories
   */
  grantAllConsent(): void {
    this.grantConsent(['analytics', 'marketing', 'personalization']);
  }

  /**
   * Deny all non-essential consent categories
   */
  denyAllConsent(): void {
    this.denyConsent(['analytics', 'marketing', 'personalization']);
  }

  /**
   * Subscribe to consent changes
   */
  subscribe(callback: ConsentCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback(this.getState()));
  }

  /**
   * Log consent event for audit trail
   */
  private logConsentEvent(categories: ConsentCategory[], granted: boolean): void {
    const event: ConsentEvent = {
      category: categories[0], // Log first category if multiple
      granted,
      timestamp: new Date().toISOString(),
    };

    // Store last 100 consent events for audit purposes
    try {
      const eventsKey = 'flashread_consent_events';
      const existing = localStorage.getItem(eventsKey);
      const events: ConsentEvent[] = existing ? JSON.parse(existing) : [];
      events.push(event);
      
      // Keep only last 100 events
      if (events.length > 100) {
        events.shift();
      }
      
      localStorage.setItem(eventsKey, JSON.stringify(events));
    } catch (e) {
      console.warn('Failed to log consent event:', e);
    }
  }

  /**
   * Cleanup data from denied categories
   */
  private cleanupDeniedData(categories: ConsentCategory[]): void {
    categories.forEach(category => {
      switch (category) {
        case 'analytics':
          this.clearAnalyticsData();
          break;
        case 'marketing':
          this.clearMarketingData();
          break;
        case 'personalization':
          this.clearPersonalizationData();
          break;
      }
    });
  }

  /**
   * Clear analytics-related data
   */
  private clearAnalyticsData(): void {
    // Clear any analytics cookies or storage
    this.clearCookiesByPattern(['_ga', '_gid', '_gat', 'utm_']);
  }

  /**
   * Clear marketing-related data
   */
  private clearMarketingData(): void {
    // Clear marketing cookies and storage
    this.clearCookiesByPattern(['_fbp', 'fr', 'tr']);
  }

  /**
   * Clear personalization-related data
   */
  private clearPersonalizationData(): void {
    // Clear personalization preferences (except necessary ones)
    const keysToKeep = ['flashread_theme', 'flashread_wpm', 'flashread_consent_v2'];
    
    try {
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (!keysToKeep.includes(key) && key.startsWith('flashread_')) {
          // Check if it's a personalization-related key
          if (key.includes('pref') || key.includes('custom') || key.includes('recommend')) {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (e) {
      console.warn('Failed to clear personalization data:', e);
    }
  }

  /**
   * Clear cookies matching patterns
   */
  private clearCookiesByPattern(patterns: string[]): void {
    try {
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        patterns.forEach(pattern => {
          if (name.startsWith(pattern) || name === pattern.replace('_', '')) {
            // Clear cookie by setting expiration in the past
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        });
      });
    } catch (e) {
      console.warn('Failed to clear cookies:', e);
    }
  }

  /**
   * Reset consent (for testing or policy updates)
   */
  resetConsent(): void {
    this.state = { ...DEFAULT_CONSENT };
    this.saveConsent();
    this.notifyListeners();
  }

  /**
   * Get consent state for display in UI
   */
  getConsentSummary(): {
    hasConsented: boolean;
    acceptedCategories: ConsentCategory[];
    deniedCategories: ConsentCategory[];
    timestamp: string | null;
  } {
    const accepted: ConsentCategory[] = [];
    const denied: ConsentCategory[] = [];

    (['analytics', 'marketing', 'personalization'] as ConsentCategory[]).forEach(cat => {
      if (this.state[cat]) {
        accepted.push(cat);
      } else {
        denied.push(cat);
      }
    });

    return {
      hasConsented: this.hasConsentChoice(),
      acceptedCategories: accepted,
      deniedCategories: denied,
      timestamp: this.state.timestamp,
    };
  }
}

// Singleton instance
export const consentManager = new ConsentManager();

// React hook for using consent state
export function useConsent() {
  return consentManager;
}
